import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface Props {
  open: boolean;
  onClose: () => void;
  variant?: "addata" | "literature" | "rummy";
}

export const RulesModal = ({ open, onClose, variant = "addata" }: Props) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "rgba(10, 10, 10, 0.93)",
          backdropFilter: "blur(10px)",
          color: "white",
          maxHeight: "85vh",
          border: "1px solid rgba(255,255,255,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {variant === "literature"
            ? "Literature Rules "
            : variant === "rummy"
              ? "Rummy Rules "
              : "Addata Rules"}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.7)" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ borderColor: "rgba(255,255,255,0.1)", pt: 2 }}
      >
        {variant === "rummy" ? (
          <>
            <Typography variant="body2" sx={{ mb: 2, opacity: 0.85, lineHeight: 1.7, fontStyle: "italic" }}>
              Rummy here is in <strong>beta</strong> and follows a German-style table rummy close to{" "}
              <Box component="a" href="https://www.rummy-palace.com/how-to-play-rummy/" sx={{ color: "#90caf9" }}>
                Rummy Palace
              </Box>{" "}
              rules (two full decks plus jokers, melds on the table, initial 30-point meld, go out by discarding your
              last card).
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, lineHeight: 1.7 }}>
              On your turn: draw from the stock or take the face-up discard, optionally lay valid melds (groups of same
              rank or runs in a suit), then discard one card. First player to empty their hand after a discard wins the
              round; opponents lose points for deadwood in hand.
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, lineHeight: 1.7 }}>
              This build does not yet support every Palace option (e.g. adding to existing melds on the table). Scores
              accumulate across rounds; use <strong>Play again</strong> when a round ends.
            </Typography>
          </>
        ) : variant === "literature" ? (
          <>
            <Typography variant="body2" sx={{ mb: 2, opacity: 0.85, lineHeight: 1.7, fontStyle: "italic" }}>
              Literature is in <strong>beta</strong>. You may run into bugs or rough edges — thanks for trying it
              out.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, lineHeight: 1.7 }}>
              Six players in two teams (alternating seats). The deck has 54 cards: low half-suits (2–7) and
              high half-suits (9–A) in each suit, plus four 8s and two jokers forming the ninth set.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, lineHeight: 1.7 }}>
              On your turn you may <strong>ask</strong> an opponent for a specific card: you must already hold a
              card from that half-suit, you cannot ask for a card you hold, and they must still have cards. If
              they have it, you take it and keep the turn; otherwise it becomes their turn.
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
              You may <strong>declare</strong> a full half-suit by assigning each card to the seat that holds it.
              If correct, your team scores a point; if wrong, the other team scores. First team to 5 points wins.
            </Typography>
          </>
        ) : (
          <>
        {/* The Deck */}
        <Typography variant="h6" sx={{ mb: 1, color: "#90caf9" }}>
          The Deck
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, lineHeight: 1.7 }}>
          Addata uses a 24-card deck — the 9, J, Q, K, 10, and Ace in each of
          the 4 suits. For 8+ players, multiple decks are used automatically.
        </Typography>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

        {/* Card Points */}
        <Typography variant="h6" sx={{ mb: 1, color: "#90caf9" }}>
          Card Points
        </Typography>
        <Table size="small" sx={{ mb: 1.5 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  borderColor: "rgba(255,255,255,0.1)",
                }}
              >
                Card
              </TableCell>
              <TableCell
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  borderColor: "rgba(255,255,255,0.1)",
                }}
              >
                Points
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              ["Ace", "11"],
              ["10", "10"],
              ["King", "3"],
              ["Queen", "2"],
              ["Jack", "1"],
              ["9", "0"],
            ].map(([card, pts]) => (
              <TableRow key={card}>
                <TableCell
                  sx={{ color: "white", borderColor: "rgba(255,255,255,0.08)" }}
                >
                  {card}
                </TableCell>
                <TableCell
                  sx={{
                    color: "#FFD700",
                    fontWeight: "bold",
                    borderColor: "rgba(255,255,255,0.08)",
                  }}
                >
                  {pts}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, lineHeight: 1.7 }}>
          <strong>Trump bonus:</strong> Trump Jack = 20 pts, Trump Nine = 14 pts
          (instead of their base 1 and 0).
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.7, fontStyle: "italic" }}>
          Total marks per deck = 141 (81 non-trump + 60 trump).
        </Typography>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

        {/* Teams */}
        <Typography variant="h6" sx={{ mb: 1, color: "#90caf9" }}>
          Teams
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, lineHeight: 1.7 }}>
          Players are split into two equal teams by seating position. Even seats
          (0, 2, 4…) form{" "}
          <Box component="span" sx={{ color: "#66bb6a", fontWeight: "bold" }}>
            Team A
          </Box>
          ; odd seats (1, 3, 5…) form{" "}
          <Box component="span" sx={{ color: "#ffa726", fontWeight: "bold" }}>
            Team B
          </Box>
          .
        </Typography>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

        {/* Bidding */}
        <Typography variant="h6" sx={{ mb: 1, color: "#90caf9" }}>
          Bidding
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, lineHeight: 1.7 }}>
          Starting from the player after the dealer, each player says{" "}
          <strong>Atta</strong> (I'll call) or <strong>Pass</strong>.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, lineHeight: 1.7 }}>
          The <strong>last</strong> player to say Atta becomes the{" "}
          <strong>caller</strong>. The stake increases by 1 for each Atta beyond
          the first (max 6).
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, lineHeight: 1.7 }}>
          If everyone passes, cards are re-dealt with the next player as dealer.
        </Typography>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

        {/* Trump */}
        <Typography variant="h6" sx={{ mb: 1, color: "#90caf9" }}>
          Trump
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, lineHeight: 1.7 }}>
          The caller picks the trump suit. Trump cards beat all non-trump cards
          regardless of rank.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, lineHeight: 1.7 }}>
          <strong>Trump rank order (lowest → highest):</strong>
          <br />
          10 → Q → K → A → 9 → J
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 2, opacity: 0.7, fontStyle: "italic", lineHeight: 1.7 }}
        >
          The trump 9 and J are the two most powerful cards — they beat even the
          trump Ace! The trump 10 is the weakest trump of all.
        </Typography>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

        {/* Playing */}
        <Typography variant="h6" sx={{ mb: 1, color: "#90caf9" }}>
          Playing Rounds
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, lineHeight: 1.7 }}>
          The caller leads the first round. You may always play{" "}
          <strong>trump</strong> — even if you have cards of the led suit.
          If you have the led suit but no playable trump, you must follow suit.
          Off-suit non-trump cards may only be played when void in the led suit.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, lineHeight: 1.7 }}>
          <strong>Overtrump rule (non-trump-led rounds only):</strong> Once a
          trump card is played in a round, any subsequent trump play must beat
          the current highest trump in that round. If you cannot beat it, you
          must play the led suit or an off-suit card instead.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, lineHeight: 1.7 }}>
          The highest trump wins the round. If no trump was played, the highest
          card in the led suit wins. The winner leads the next round.
        </Typography>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

        {/* Last Round Bonus */}
        <Typography variant="h6" sx={{ mb: 1, color: "#90caf9" }}>
          Last Round Bonus
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, lineHeight: 1.7 }}>
          The team that wins the <strong>last round</strong> receives a bonus of{" "}
          <strong>5 × number of decks</strong> points (5 for 1 deck, 10 for 2
          decks, 15 for 3 decks).
        </Typography>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

        {/* Winning */}
        <Typography variant="h6" sx={{ mb: 1, color: "#90caf9" }}>
          Winning
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, lineHeight: 1.7 }}>
          After all rounds are played, each team counts the points in the rounds
          they won (including the last round bonus).
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, lineHeight: 1.7 }}>
          <strong>Win target = ⅓ of total points (rounded)</strong> — e.g. 49
          for 1 deck, 97 for 2 decks, 146 for 3 decks.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, lineHeight: 1.7 }}>
          The <strong>opposition</strong> (non-calling team) wins if their marks
          ≥ win target.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, lineHeight: 1.7 }}>
          The <strong>calling team</strong> wins by holding the opposition below
          that target.
        </Typography>
        <Typography
          variant="body2"
          sx={{ opacity: 0.6, fontStyle: "italic", lineHeight: 1.7 }}
        >
          The stake (1–6) reflects how competitive the bidding was.
        </Typography>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
